interface RetryConfig {
  maxAttempts: number;
  delayMs: number;
  backoffFactor: number;
}

export class RetryService {
  private static defaultConfig: RetryConfig = {
    maxAttempts: 3,
    delayMs: 1000,
    backoffFactor: 1.5
  };

  static async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const finalConfig = { ...this.defaultConfig, ...config };
    let lastError: Error | null = null;
    let delay = finalConfig.delayMs;

    for (let attempt = 1; attempt <= finalConfig.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // Se for o último erro, não precisa esperar
        if (attempt === finalConfig.maxAttempts) break;

        // Log do erro com informações úteis
        console.warn(
          `Attempt ${attempt} failed. Retrying in ${delay}ms...`,
          error instanceof Error ? error.message : error
        );

        // Espera antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Aumenta o delay para a próxima tentativa
        delay *= finalConfig.backoffFactor;
      }
    }

    throw new Error(
      `Operation failed after ${finalConfig.maxAttempts} attempts. Last error: ${lastError?.message}`
    );
  }
}

// Função auxiliar para verificar se um erro é recuperável
export function isRetryableError(error: any): boolean {
  // Erros de rede geralmente são recuperáveis
  if (error.name === 'NetworkError') return true;
  
  // Erros de rate limit são recuperáveis
  if (error.status === 429) return true;
  
  // Erros de servidor (5xx) são recuperáveis
  if (error.status >= 500 && error.status < 600) return true;
  
  // Erros de timeout são recuperáveis
  if (error.code === 'ETIMEDOUT' || error.code === 'ECONNRESET') return true;
  
  return false;
}
